import { useEffect, useState } from 'react';
import { Modal, Space, Typography, Button, List, Divider, Checkbox, message, InputNumber, Row, Col, Spin } from 'antd';
import { ArrowLeftOutlined, CloseCircleOutlined } from '@ant-design/icons';
import { useAuth } from '../../context/auth.context'; // Assuming you have access to auth context
import { useHistory } from 'react-router-dom';

const { Title, Text } = Typography;

const loadRazorpayScript = () => {
    return new Promise((resolve) => {
        if (window.Razorpay) {
            resolve(true);
            return;
        }

        const script = document.createElement('script');
        script.src = 'https://checkout.razorpay.com/v1/checkout.js';
        script.onload = () => {
            resolve(true);
        };
        script.onerror = () => {
            resolve(false);
        };
        document.body.appendChild(script);
    });
};

export default function ShowingAddon({
    open,
    onBack,
    featureKey,
    loading,
    onUpgradePlan,
    subscriptionData,
    customAddons = [],
    userCurrency,
}) {
    const [selectedAddonIds, setSelectedAddonIds] = useState([]);
    const [currencyType, setCurrencyType] = useState('$');
    const [addonQuantities, setAddonQuantities] = useState({});
    const [paymentLoading, setPaymentLoading] = useState(false);
    const [pollingForUpdate, setPollingForUpdate] = useState(false);
    const [showLoadingModal, setShowLoadingModal] = useState(false);
    const [initialLimit, setInitialLimit] = useState('');
    const history = useHistory();
    const [priceData, setPriceData] = useState(null);
    const { currentUser, apiClient, fetchUserSubscriptionPlan } = useAuth();
    console.log('pricedata', priceData);

    const selectedFeature = subscriptionData?.features?.find((f) => f.key === featureKey);
    const addons =
        customAddons.length > 0
            ? customAddons // Use passed addons if provided
            : selectedFeature?.feature?.addons || [];

    useEffect(() => {
        if (subscriptionData?.currency === 'INR') {
            setCurrencyType('₹');
        } else if (subscriptionData?.currency === 'USD') {
            setCurrencyType('$');
        } else if (subscriptionData?.currency === 'EUR') {
            setCurrencyType('€');
        }
        else if (subscriptionData?.currency === 'SGD') {
            setCurrencyType('$');
        } 
    }, [subscriptionData]);

    const formatPrice = (currency, price) => {
        const symbols = {
            INR: '₹',
            USD: '$',
            EUR: '€',
            SGD: '$',
        };
        return `${symbols[currency] || currency}${price}`;
    };

    // Handle modal close - reset state and call onBack
    const handleModalClose = () => {
        if (!paymentLoading && !pollingForUpdate) {
            setSelectedAddonIds([]);
            setAddonQuantities({});
            onBack();
        }
    };

    const toggleAddonSelection = (addonId) => {
        setSelectedAddonIds((prev) => {
            const updated = prev.includes(addonId) ? prev.filter((id) => id !== addonId) : [...prev, addonId];
            if (!prev.includes(addonId)) {
                setAddonQuantities((q) => ({ ...q, [addonId]: 1 }));
            } else {
                setAddonQuantities((q) => {
                    const newQuantities = { ...q };
                    delete newQuantities[addonId];
                    return newQuantities;
                });
            }
            return updated;
        });
    };

    const pollSubscriptionUpdate = async () => {
        setPollingForUpdate(true);
        setShowLoadingModal(true);

        // Wait for 20 seconds, then fetch subscription and close loading
        setTimeout(async () => {
            try {
                console.log('Fetching updated subscription after 20 seconds...');
                await fetchUserSubscriptionPlan();
                setSelectedAddonIds([]);
                setAddonQuantities({});
                message.success('Addon purchase completed! Your subscription has been updated.');
            } catch (error) {
                console.error('Error fetching subscription update:', error);
                message.warning('Purchase completed but failed to fetch updated limits. Please refresh the page.');
            } finally {
                setPollingForUpdate(false);
                setShowLoadingModal(false);
                onBack();
            }
        }, 20000);
    };

    const handlePaymentSuccess = async (paymentResponse) => {
        console.log('Addon payment successful:', paymentResponse);
        setPaymentLoading(false);
        pollSubscriptionUpdate();
    };

    const handleBuy = async () => {
        if (selectedAddonIds.length === 0) {
            message.warning('Please select at least one addon to purchase.');
            return;
        }
        setInitialLimit(selectedFeature.totalLimit);
        console.log('Total Limit', initialLimit);
        try {
            setPaymentLoading(true);

            const scriptLoaded = await loadRazorpayScript();
            if (!scriptLoaded) {
                message.error('Failed to load payment system. Please try again.');
                setPaymentLoading(false);
                return;
            }
            const addonsPayload = selectedAddonIds.map((id) => ({
                addonId: id,
                quantity: addonQuantities[id] || 1,
            }));
            console.log('Purchasing addons:', addonsPayload);
            // call the purchase addon API
            const response = await apiClient.post('/api/subscriptions/purchase-addons', {
                addons: addonsPayload,
            });

            const data = response.data?.data[0];
            const addonPurchaseDraft = data?.addonPurchaseDraft;
            const razorpayPayment = data?.razorpayPayment;

            if (razorpayPayment && addonPurchaseDraft) {
                const options = {
                    key: process.env.REACT_APP_RAZORPAY_KEY_ID,
                    order_id: razorpayPayment.id,
                    amount: razorpayPayment.amount,
                    name: 'CommKraft',
                    description: `Addon Purchase - ${selectedFeature?.name}`,
                    image: '/logo192.png',
                    handler: function (response) {
                        console.log('Payment successful:', response);
                        handlePaymentSuccess(response, addonPurchaseDraft);
                    },
                    prefill: {
                        name: currentUser?.firstName + ' ' + currentUser?.lastName || '',
                        email: currentUser?.email || '',
                        contact: currentUser?.phoneNumber || '',
                    },
                    notes: {
                        addonPurchaseDraftId: addonPurchaseDraft.id.toString(),
                        organizationId: currentUser?.organizationId?.toString(),
                        featureKey: featureKey,
                        addonCount: selectedAddonIds.length,
                    },
                    theme: {
                        color: '#1890ff',
                    },
                    modal: {
                        ondismiss: function () {
                            console.log('Payment modal dismissed');
                            setPaymentLoading(false);
                        },
                    },
                    retry: {
                        enabled: true,
                        max_count: 3,
                    },
                };
                const rzp = new window.Razorpay(options);
                rzp.on('payment.failed', function (response) {
                    console.error('Payment failed:', response.error);
                    setPaymentLoading(false);
                    message.error('Payment failed. Please try again.');
                });
                rzp.open();
            } else {
                console.error('Invalid payment data received:', data);
                setPaymentLoading(false);
                message.error('Failed to create payment order. Please try again.');
            }
            setPaymentLoading(false);
        } catch (error) {
            console.error('Error purchasing addons:', error);
            setPaymentLoading(false);
            message.error('Failed to process addon purchase. Please try again.');
        }
    };
const getPriceForUser = (addon) => {
    if (!addon?.prices || addon.prices.length === 0) return null;
    return addon.prices.find((p) => p.currency === userCurrency);
};

const calculateTotalCost = () => {
    return selectedAddonIds.reduce((total, addonId) => {
        const addon = addons.find((a) => a.id === addonId);
        const quantity = addonQuantities[addonId] || 1;
        const priceData = getPriceForUser(addon);
        const price = priceData?.unitPrice || 0;
        return total + price * quantity;
    }, 0);
};

    const LoadingModal = () => {
        const [currentMessageIndex, setCurrentMessageIndex] = useState(0);
        const messages = [
            'Processing your addon purchase...',
            'Updating your subscription limits...',
            'Almost ready! Just a moment more...',
        ];

        useEffect(() => {
            if (!showLoadingModal) return;

            const interval = setInterval(() => {
                setCurrentMessageIndex((prev) => (prev + 1) % messages.length);
            }, 2500);

            return () => clearInterval(interval);
        }, [showLoadingModal]);

        return (
            <Modal
                open={showLoadingModal}
                centered
                closable={true}
                maskClosable={false}
                onCancel={() => {
                    setShowLoadingModal(false);
                    setPollingForUpdate(false);
                }}
                footer={null}
                zIndex={999999}
                width={400}
            >
                <Space direction="vertical" size="large" style={{ width: '100%', textAlign: 'center' }} align="center">
                    <Spin size="large" />
                    <Title level={4} style={{ margin: 0 }}>
                        Processing Purchase...
                    </Title>
                    <Text type="secondary" style={{ fontSize: '14px' }}>
                        {messages[currentMessageIndex]}
                    </Text>
                </Space>
            </Modal>
        );
    };

    return (
        <>
            <LoadingModal />
            <Modal
                open={open}
                centered
                closable={true}
                maskClosable={true}
                onCancel={handleModalClose}
                footer={null}
                zIndex={999999}
                width={600}
            >
                <Space direction="vertical" style={{ width: '100%' }} size="large">
                    <div style={{ textAlign: 'center' }}>
                        <Title level={3}>{selectedFeature?.name?.toUpperCase()}</Title>
                        <Text type="secondary">{selectedFeature?.description?.toUpperCase()}</Text>
                    </div>

                    {addons.length > 0 ? (
                        <>
                            <List
                                itemLayout="vertical"
                                dataSource={addons}
                                renderItem={(addon) => (
                                    <List.Item
                                        style={{
                                            borderBottom: '1px solid #f0f0f0',
                                        }}
                                    >
                                        <Space style={{ width: '100%' }} size={4}>
                                            <Checkbox
                                                checked={selectedAddonIds.includes(addon.id)}
                                                onChange={() => toggleAddonSelection(addon.id)}
                                                style={{ fontSize: 13 }}
                                                disabled={paymentLoading || pollingForUpdate || !getPriceForUser(addon)}
                                            >
                                                <Space size={25}>
                                                    <Text style={{ fontSize: 13 }}>
                                                        Unit Limit: <strong>{addon.unitLimit}</strong>
                                                    </Text>
                                                    <Space direction="vertical" size={0}>
                                                        {addon.prices?.length > 0 ? (
                                                            <Text style={{ fontSize: 13 }}>
                                                                {(() => {
                                                                    const addonPriceData = getPriceForUser(addon);
                                                                    console.log('addonPriceData', addonPriceData);
                                                                    return addonPriceData
                                                                        ? formatPrice(
                                                                              addonPriceData.currency,
                                                                              addonPriceData.unitPrice,
                                                                          )
                                                                        : 'No pricing available';
                                                                })()}
                                                            </Text>
                                                        ) : (
                                                            <Text type="secondary" style={{ fontSize: 13 }}>
                                                                No pricing available
                                                            </Text>
                                                        )}
                                                    </Space>

                                                    {selectedAddonIds.includes(addon.id) &&
                                                        selectedFeature.type !== 'boolean' && (
                                                            <div style={{ marginTop: 4 }}>
                                                                <Text style={{ fontSize: 13, marginRight: 6 }}>
                                                                    Qty:
                                                                </Text>
                                                                <InputNumber
                                                                 
                                                                    min={1}
                                                                    size="small"
                                                                    value={addonQuantities[addon.id] || 1}
                                                                    onChange={(value) =>
                                                                        setAddonQuantities((prev) => ({
                                                                            ...prev,
                                                                            [addon.id]: value,
                                                                        }))
                                                                    }
                                                                    disabled={paymentLoading || pollingForUpdate}
                                                                />
                                                            </div>
                                                        )}
                                                </Space>
                                            </Checkbox>
                                        </Space>
                                    </List.Item>
                                )}
                            />

                            {/* Total Cost Display */}
                            {selectedAddonIds.length > 0 && (
                                <div
                                    style={{
                                        padding: '16px',
                                        backgroundColor: '#f5f5f5',
                                        borderRadius: '8px',
                                        textAlign: 'center',
                                    }}
                                >
                                    <Text strong style={{ fontSize: '16px' }}>
                                        Total Cost: {currencyType} {calculateTotalCost()}
                                    </Text>
                                </div>
                            )}
                        </>
                    ) : (
                        <Text type="secondary" style={{ textAlign: 'center', display: 'block' }}>
                            No addons available for this feature.
                        </Text>
                    )}

                    <Space style={{ justifyContent: 'center', width: '100%' }}>
                        {addons.length > 0 && (
                            <Button
                                type={selectedAddonIds.length > 0 ? 'primary' : 'default'}
                                size="large"
                                onClick={handleBuy}
                                loading={paymentLoading}
                                disabled={paymentLoading || selectedAddonIds.length === 0 || pollingForUpdate}
                            >
                                {pollingForUpdate
                                    ? 'Processing Purchase...'
                                    : paymentLoading
                                    ? 'Processing Payment...'
                                    : 'Buy Addon'}
                            </Button>
                        )}
                        <Button
                            type={addons.length <= 0 ? 'primary' : 'default'}
                            size="large"
                            onClick={() => {
                                history.push('/admin/my-subscription');
                            }}
                            disabled={paymentLoading || pollingForUpdate}
                        >
                            View Plan
                        </Button>
                    </Space>
                </Space>
            </Modal>
        </>
    );
}
